import { getSelectedTicketsFromOrderAttendees } from '../../containers/ticketSelection/utils/ticketUtils';
import { Ticket, TicketId } from '../../models/tickets';
import { CheckoutState } from '../../state';
import { getIsReservedSeating } from '../event';
import { getAttendees, hasExistingOrder } from '../order/typedOrderSelectors';
import { createSelector } from '../utils/selector';
import { getTicketIds, getTicketsById } from './pureSelectors';

/**
 * Reserved seating was initially built to get selectedTickets from the list of attendees, since
 * the selected ticket quantity is not always updated in redux state in the Reserved flow - at least
 * one example is when users select seats through Pick-a-seat.
 * This means that in the reserved flow, only if we have an order *and* the order has atttendees do
 * we get selected tickets from order.attendees rather than ticketsById.
 *
 * TODO: Consider properly updating selected ticket quantity via Redux in all reserved flows. There
 * should be a consistent way to calculate selectedTickets no matter which flow we are in.
 *
 * In the case of an Existing Order, the order has already been created externally.
 * The usual ticket selection step has therefore been skipped, and the redux state could not be updated properly.
 * The ONLY way to retrieve the selected tickets is through `order.attendees`
 * The logic to do so is the same as for reserved seating.
 */
const shouldGetTicketsFromOrder = (state: CheckoutState) =>
    (getIsReservedSeating(state) || hasExistingOrder(state)) &&
    getAttendees(state).length > 0;

const getSelectedTicketsFromTicketsById = createSelector(
    getTicketIds,
    getTicketsById,
    ((ticketIds: Array<TicketId>, tickets: Record<TicketId, Ticket>) =>
        ticketIds
            .map((id) => tickets[id])
            .filter(
                ({ selectedQuantity, variants = [] }) =>
                    selectedQuantity > 0 ||
                    variants.some((variant) => variant.selectedQuantity > 0),
            )) as unknown as any,
);

export const getSelectedTickets = (state: CheckoutState) =>
    shouldGetTicketsFromOrder(state)
        ? getSelectedTicketsFromOrderAttendees(state)
        : (getSelectedTicketsFromTicketsById(state) as Array<Ticket>);
